import { useContext, useState } from "react";
import {
  Box,
  Collapse,
  Icon,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { NavLink, useNavigate } from "react-router-dom";
import { AuthContext } from "src/utils/AuthContext.js";
import NavMenuItem from "src/components/blocks/NavMenuItem.js";
import SignInDialog from "src/components/blocks/dialogs/SignInDialog.js";
import { getPageRoute, hasValidConsumerSubscription } from "src/services";
import { trackNavItemClicked } from "src/utils/sectionAnalytics.js";
import { useBrowseSeen } from "src/utils/useBrowseSeen.js";
import { useIsUsRegion } from "src/utils/useIsUsRegion.js";
import BrowseTree from "./BrowseTree.js";
import SidebarUpgradeCard from "./SidebarUpgradeCard.js";
import { HOME_ITEM, SIDEBAR_SECTIONS } from "./navItems.js";

export const SIDEBAR_WIDTH = 272;
export const COMPACT_SIDEBAR_WIDTH = 96;

/**
 * Collapsible navigation for the discovery surfaces. Every entry routes
 * somewhere real — there are no placeholder items.
 *
 * @param {{ compact?: boolean, onNavigate?: () => void, onBrowseClick?: () => void,
 *   variant?: "light"|"dark", testId?: string }} props - `compact` renders the
 *   persistent desktop rail; `onNavigate` closes transient menus; `onBrowseClick`
 *   opens the browse tree from the rail's Browse item; `variant` matches the
 *   shell's theme so the rail reads on a dark page.
 */
const DiscoverySidebar = ({ compact = false, onNavigate, onBrowseClick, onCollapse, variant = "light", testId = "discovery-sidebar" }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isUs = useIsUsRegion();
  const isDark = variant === "dark";
  const [collapsed, setCollapsed] = useState({});
  const [openSignInDialog, setOpenSignInDialog] = useState(false);
  const [browseSeen, markBrowseSeen] = useBrowseSeen();
  const libraryRoute = getPageRoute("library", "LIBRARY");

  const toggle = (key) =>
    setCollapsed((previous) => ({ ...previous, [key]: !previous[key] }));

  const visibleItems = (items) =>
    items.filter(
      (item) => (!item.requiresAuth || user) && (!item.usOnly || isUs),
    );
  const isLibraryItem = (item) => item.to === libraryRoute;
  const trackItem = (item) =>
    trackNavItemClicked({
      user,
      surface: compact ? "sidebar-rail" : "sidebar",
      item: item.key ?? item.to,
    });
  const resolveTo = (item) =>
    item.memberTo && hasValidConsumerSubscription(user) ? item.memberTo : item.to;
  const handleLibraryClick = (event) => {
    if (user) {
      onNavigate?.();
      return;
    }

    event.preventDefault();
    setOpenSignInDialog(true);
  };
  const handleLibrarySignIn = () => {
    navigate(libraryRoute);
    onNavigate?.();
  };
  // Categories are expand-only now (no landing page), so the skinny rail shows
  // Home, a Browse entry to the tree, then the actionable destinations.
  const compactItems = [
    HOME_ITEM,
    { key: "browse", label: "Browse", icon: "grid_view", action: "browse", hasBadge: true },
    ...SIDEBAR_SECTIONS.filter((section) => section.key !== "explore").flatMap((section) =>
      visibleItems(section.items),
    ),
  ];

  if (compact) {
    return (
      <Box
        component="nav"
        data-testid={testId}
        aria-label="Compact discovery navigation"
        sx={{ boxSizing: "border-box", width: "100%", py: 1, px: 0.5, color: isDark ? "white" : "text.primary" }}
      >
        <List disablePadding>
          {compactItems.map((item) => (
            <Tooltip key={item.key ?? item.to} title={item.label} placement="right">
              <ListItemButton
                {...(item.action === "browse"
                  ? {
                      onClick: () => {
                        trackItem(item);
                        markBrowseSeen();
                        onBrowseClick?.();
                      },
                      "data-testid": "sidebar-browse",
                    }
                  : isLibraryItem(item)
                    ? {
                        component: NavLink,
                        to: item.to,
                        "data-testid": item.testId,
                        onClick: (event) => {
                          trackItem(item);
                          handleLibraryClick(event);
                        },
                      }
                    : {
                        component: NavLink,
                        to: resolveTo(item),
                        "data-testid": item.testId,
                        onClick: () => {
                          trackItem(item);
                          onNavigate?.();
                        },
                      })}
                aria-label={item.label}
                sx={{
                  minHeight: 64,
                  mb: 0.5,
                  borderRadius: "8px",
                  flexDirection: "column",
                  justifyContent: "center",
                  px: 0.5,
                  py: 0.75,
                  position: "relative",
                  color: isDark ? "overlay.white70" : "text.secondary",
                  // Match the mobile tab bar: edge indicator + filled icon
                  // instead of a background pill.
                  "& .material-symbols-outlined": { fontVariationSettings: "'FILL' 0, 'wght' 300" },
                  "&.active": { color: isDark ? "white" : "text.primary" },
                  "&.active .material-symbols-outlined": { fontVariationSettings: "'FILL' 1, 'wght' 400" },
                  "&.active .MuiListItemText-primary": { fontWeight: 700 },
                  "&.active::before": {
                    content: '""',
                    position: "absolute",
                    right: "-4px",
                    top: 0,
                    width: "3px",
                    height: "100%",
                    borderRadius: "3px 0 0 3px",
                    backgroundColor: isDark ? "white" : "text.primary",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, mb: 0.25, color: "inherit", position: "relative" }}>
                  <Icon baseClassName="material-symbols-outlined" fontSize="medium">
                    {item.icon}
                  </Icon>
                  {item.hasBadge && !browseSeen ? (
                    <Typography
                      component="span"
                      data-testid="sidebar-badge-new"
                      sx={{
                        position: "absolute",
                        top: "-8px",
                        left: "calc(100% - 2px)",
                        px: "5px",
                        py: "1px",
                        borderRadius: "8px",
                        fontSize: "0.55rem",
                        fontWeight: 700,
                        lineHeight: 1.3,
                        letterSpacing: "0.02em",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        color: isDark ? "#000" : "#fff",
                        backgroundColor: isDark ? "#fff" : "#000",
                      }}
                    >
                      New
                    </Typography>
                  ) : item.hasBadge && browseSeen ? (
                    <Box
                      component="span"
                      data-testid="sidebar-badge-dot"
                      sx={{
                        position: "absolute",
                        top: "-4px",
                        left: "calc(100% - 1px)",
                        width: "7px",
                        height: "7px",
                        borderRadius: "50%",
                        backgroundColor: isDark ? "white" : "#000",
                      }}
                    />
                  ) : null}
                </ListItemIcon>
                <ListItemText
                  primary={item.compactLabel ?? item.label}
                  primaryTypographyProps={{
                    variant: "caption",
                    noWrap: true,
                    sx: { display: "block", maxWidth: "100%", fontSize: "0.65rem", lineHeight: 1.15 },
                  }}
                />
              </ListItemButton>
            </Tooltip>
          ))}
        </List>
        <SignInDialog
          open={openSignInDialog}
          handleClose={() => setOpenSignInDialog(false)}
          onSignIn={handleLibrarySignIn}
        />
      </Box>
    );
  }

  return (
    <Box
      component="nav"
      data-testid={testId}
      aria-label="Discovery navigation"
      sx={{ boxSizing: "border-box", width: "100%", py: 2, px: 1 }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <List disablePadding sx={{ flex: 1, minWidth: 0 }}>
          <NavMenuItem
            component={NavLink}
            to={HOME_ITEM.to}
            data-testid={HOME_ITEM.testId}
            onClick={() => {
              trackItem(HOME_ITEM);
              onNavigate?.();
            }}
            icon={HOME_ITEM.icon}
            label={HOME_ITEM.label}
          />
        </List>
        {onCollapse && (
          <Tooltip title="Collapse navigation">
            <IconButton
              size="small"
              onClick={onCollapse}
              aria-label="Collapse navigation"
              data-testid="sidebar-collapse"
              sx={{ ml: 0.5 }}
            >
              <Icon
                baseClassName="material-symbols-outlined"
                sx={{ fontSize: "1.35rem", color: "text.secondary" }}
              >
                left_panel_close
              </Icon>
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {SIDEBAR_SECTIONS.map((section) => (
        <Box key={section.key} sx={{ mb: 1.5 }}>
          <ListItemButton
            onClick={() => toggle(section.key)}
            aria-expanded={!collapsed[section.key]}
            sx={{ borderRadius: "8px", py: 0.5 }}
          >
            <ListItemText
              primary={
                <Typography
                  variant="subtitle2"
                  sx={{ textTransform: "uppercase", letterSpacing: "0.5px", color: "text.quaternary" }}
                >
                  {section.label}
                </Typography>
              }
            />
            <Icon baseClassName="material-symbols-outlined" sx={{ color: "text.quaternary" }}>
              {collapsed[section.key] ? "expand_more" : "expand_less"}
            </Icon>
          </ListItemButton>
          <Collapse in={!collapsed[section.key]} unmountOnExit>
            {section.key === "explore" ? (
              <BrowseTree onNavigate={onNavigate} testId="sidebar-browse-tree" />
            ) : (
              <List disablePadding>
                {visibleItems(section.items).map((item) => (
                  <NavMenuItem
                    key={item.to}
                    component={NavLink}
                    to={resolveTo(item)}
                    data-testid={item.testId}
                    onClick={(event) => {
                      trackItem(item);
                      if (isLibraryItem(item)) handleLibraryClick(event);
                      else onNavigate?.();
                    }}
                    icon={item.icon}
                    label={item.label}
                  />
                ))}
              </List>
            )}
          </Collapse>
        </Box>
      ))}
      <SidebarUpgradeCard />
      <SignInDialog
        open={openSignInDialog}
        handleClose={() => setOpenSignInDialog(false)}
        onSignIn={handleLibrarySignIn}
      />
    </Box>
  );
};

export default DiscoverySidebar;
